/*
 * Here is the VisBook booking URL documentation:
 *
 * https://reservations.visbook.com/404/search?lang=en&checkIn=2020-11-17&checkOut=2020-11-20
 * https://reservations.visbook.com/404/search?lang=en&checkIn=2020-11-17&checkOut=2020-11-20&webProductId=%5B96296%5D
 * https://reservations.visbook.com/404/search?lang=en&checkIn=2020-11-16&checkOut=2020-11-19&webProductId=%5B4260,4261%5D
 *
 * Just those 3 lines. Figure it out.
 */

import type { VisBookWebproductService, VisBookWebproductsResponse } from './visbookResponseTypes';
import type {
    DirectHotelOffer,
    PaymentSettings,
    PaymentType,
    VisBookProductAndPrice,
    VisBookPaymentType,
    VisBookPaymentTypes,
} from '@/interfaces';
import {
    getInclusionsForVisBookProvider,
    getProviderForVisBookId,
    isVisBookBreakfastIncluded,
    isVisBookCancellationIncluded,
} from '@/utils/integration/visbook';
import { encrypt } from '@/server/crypto/outPageEncryption';
import { providers } from '@/utils/integration/providers';

const deepLink = (webEntityId: number, checkInDateISO: string, checkOutDateISO: string, products: number | number[] | string): string =>
    `https://reservations.visbook.com/${webEntityId}/search?lang=no&checkIn=${checkInDateISO}&checkOut=${checkOutDateISO}&webProductId=%5B${
        Array.isArray(products) ? products.join(',') : products
    }%5D`;

const getRequiredAdditionalServices = (services: VisBookWebproductService[]): VisBookWebproductService[] =>
    services.filter((service) => service.rules.minValueType === 'number' && service.rules.minValue > 0);

const getOptionalAdditionalServices = (services: VisBookWebproductService[]): VisBookWebproductService[] =>
    services.filter((service) => service.rules.minValueType !== 'number' || service.rules.minValue === 0);

const getPriceOfRequiredAdditionalServices = (services: VisBookWebproductService[]): number =>
    getRequiredAdditionalServices(services)
        .map((service) => service.price)
        .reduce((sum, value) => value + sum, 0);

export const mapVisBookOffers = (
    webEntityId: number,
    checkInDateISO: string,
    checkOutDateISO: string,
    persons: number,
    data: VisBookWebproductsResponse,
): DirectHotelOffer[] => {
    const provider = getProviderForVisBookId(webEntityId);
    return data?.accommodations
        .filter((accommodation) => accommodation.availability.available)
        .flatMap((accommodation): DirectHotelOffer[] =>
            accommodation.prices
                .filter((price) => price.priceType === 'perDay')
                .map((price, _i, prices): DirectHotelOffer => {
                    const requiredAdditionalServicePrice = getPriceOfRequiredAdditionalServices(accommodation.additionalServices);
                    return {
                        isComplete: true,
                        rooms: [
                            {
                                name: prices.length > 1 ? `${accommodation.name} - ${price.name}` : accommodation.name,
                                description: accommodation.description?.long || accommodation.description?.short || '',
                                images: accommodation.images.map((image) => `${image.transformer}/${image.imagePath}`),
                                price: price.calculatedPrice + requiredAdditionalServicePrice,
                            },
                        ],
                        provider,
                        inclusions: [
                            ...getInclusionsForVisBookProvider(provider, accommodation.properties, price),
                            ...getRequiredAdditionalServices(accommodation.additionalServices).map((svc) => `${svc.name} inkludert`),
                            ...getOptionalAdditionalServices(accommodation.additionalServices).map((svc) => `${svc.name} tilgjengelig`),
                        ],
                        totalPrice: price.calculatedPrice + requiredAdditionalServicePrice,
                        encryptedParams: encrypt({
                            deepLink: deepLink(webEntityId, checkInDateISO, checkOutDateISO, accommodation.id),
                            provider: providers[provider],
                        }),
                        prePaymentRequired: true,
                        hasFreeCancellation: isVisBookCancellationIncluded(accommodation.properties),
                        hasFreeBreakfast: isVisBookBreakfastIncluded(accommodation.properties, price),
                        integratedBooking: getRequiredAdditionalServices(accommodation.additionalServices).length === 0,
                        bookingParams: visBookBookingPageParams(accommodation.id, price.id),
                    };
                }),
        );
};

export const visBookBookingPageParams = (productId: number, priceId: number): string =>
    btoa(
        JSON.stringify({
            productId,
            priceId,
        }),
    );

export const extractVisBookBookingPageParams = (bookingParams = '{}'): VisBookProductAndPrice => {
    try {
        const data = JSON.parse(atob(bookingParams));
        return {
            productId: data.productId,
            priceId: data.priceId,
        };
    } catch (_) {
        return {};
    }
};

export const mapVisBookPaymentTypes = (paymentTypes: VisBookPaymentTypes): PaymentSettings => {
    if (paymentTypes.paymentTypes.includes('netAxept')) {
        return { type: 'netAxept' };
    }
    if (paymentTypes.paymentTypes.includes('partialPayment')) {
        if (paymentTypes.partialPayment.type === 'fixed') {
            return { type: 'partial', fixedAmount: paymentTypes.partialPayment.payNow };
        }
        if (paymentTypes.partialPayment.type === 'percentage') {
            return { type: 'partial', percentage: paymentTypes.partialPayment.payNow };
        }
    } else if (paymentTypes.paymentTypes.includes('noOnlinePayment')) {
        return { type: 'none' };
    }
};

export const getVisBookPaymentType = (paymentType: PaymentType): VisBookPaymentType => {
    if (paymentType === 'netAxept') {
        return 'netAxept';
    }
    if (paymentType === 'partial') {
        return 'partialPayment';
    }
    return 'noOnlinePayment';
};
